import { useCfmContext } from "../Context/CfmContext";

/**
 * Custom hook to update a specific field in the context state.
 *
 * @param {string} field - The name of the field in the context state to update (e.g., "pensions", "investments").
 * @returns {Object} An object containing updater functions:
 * @returns {Function} updateContextField - Updates a specific key of an item at a given index in the field array.
 * @returns {Function} addItem - Adds a new item to the field array in the context state.
 * @returns {Function} deleteItem - Removes an item at a specified index from the field array in the context state.
 *
 * @example
 * const { updateContextField, addItem, deleteItem } = useContextUpdater('pensions');
 * updateContextField(0, 'name', 'New Pension Name');
 * addItem({ name: 'Another Pension', value: 1000 });
 * deleteItem(2);
 *
 * @remarks
 * When the "name" key is updated, this hook also synchronizes the name change across related context fields
 * (such as pensions, investments, income, regularIncome, tfcPhasedRows, adHocData, dropRiskData) to maintain referential integrity.
 */
export const useContextUpdater = (field) => {
  const { state, dispatch } = useCfmContext();

  /**
   * Updates a specific field in the context state array at the given index and dispatches the update.
   * If the updated key is "name" and its value has changed, synchronizes the name change across related state arrays.
   *
   * @param {number} index - The index of the item in the state array to update.
   * @param {string} key - The key of the field to update within the item.
   * @param {*} value - The new value to set for the specified key.
   *
   * @throws {TypeError} If the target state field is not an array.
   *
   * @example
   * updateContextField(2, 'name', 'New Pension Name');
   */
  const updateContextField = (index, key, value) => {
    if (!Array.isArray(state[field])) return;

    const updated = [...state[field]];
    const oldValue = updated[index]?.[key];

    updated[index] = {
      ...updated[index],
      [key]: value,
    };

    dispatch({ type: `UPDATE_${field.toUpperCase()}`, payload: updated });

    // Only trigger name syncs if "name" field changed
    if (key === "name" && oldValue && oldValue !== value) {
      const updateNameRefs = (items) =>
        Array.isArray(items)
          ? items.map((item) =>
              item.name === oldValue ? { ...item, name: value } : item
            )
          : [];

      dispatch({
        type: "UPDATE_PENSIONS",
        payload: updateNameRefs(state.pensions),
      });
      dispatch({
        type: "UPDATE_INVESTMENTS",
        payload: updateNameRefs(state.investments),
      });
      dispatch({
        type: "UPDATE_INCOME",
        payload: updateNameRefs(state.income),
      });
      dispatch({
        type: "UPDATE_REGULARINCOME",
        payload: updateNameRefs(state.regularIncome),
      });
      dispatch({
        type: "SET_TFC_PHASED_ROWS",
        payload: updateNameRefs(state.tfcPhasedRows),
      });
      dispatch({
        type: "SET_ADHOC_DATA",
        payload: updateNameRefs(state.adHocData),
      });
      dispatch({
        type: "SET_DROPRISK_DATA",
        payload: updateNameRefs(state.dropRiskData),
      });
    }
  };

  /**
   * Adds a new item to the specified field in the state array and dispatches an update action.
   *
   * @param {any} item - The item to be added to the array in the state.
   * @returns {void}
   *
   * @example
   * addItem({ name: 'New Item', value: 42 });
   *
   * @description
   * This function retrieves the current array from the state for the given field,
   * appends the new item to it, and dispatches an action to update the state.
   * If the field is not an array, it initializes it as an empty array before adding the item.
   */
  const addItem = (item) => {
    const existing = Array.isArray(state[field]) ? state[field] : [];
    dispatch({
      type: `UPDATE_${field.toUpperCase()}`,
      payload: [...existing, item],
    });
  };

  /**
   * Deletes an item from the specified array field in the state at the given index.
   *
   * @function
   * @param {number} index - The index of the item to be deleted from the array.
   * @returns {void}
   *
   * @example
   * // Removes the item at index 2 from the state[field] array
   * deleteItem(2);
   *
   * @remarks
   * - If the state[field] is not an array, the function does nothing.
   * - Dispatches an action to update the state with the filtered array.
   */
  const deleteItem = (index) => {
    if (!Array.isArray(state[field])) return;

    const filtered = state[field].filter((_, i) => i !== index);
    dispatch({ type: `UPDATE_${field.toUpperCase()}`, payload: filtered });
  };

  return { updateContextField, addItem, deleteItem };
};
